/**
 * Design tokens — the canonical size, spacing, radius, and typography
 * buckets used by the primitives in `components/ui/`. Importing these
 * from a single source means a change to padding/radius/font-size
 * propagates everywhere automatically.
 *
 * The token names match the prop names on the primitives:
 *
 *   <Card padding="md">              → TOKENS.card.padding.md
 *   <Button size="lg" variant="...">  → TOKENS.button.sizes.lg
 *   <Input size="md">                 → TOKENS.input.sizes.md
 *
 * Colors are NOT in here — they live in CSS variables. Actions (buttons,
 * links, focus rings, checked controls, selected segments) use the
 * PLATFORM vars in index.css (`--cc-action`, `--cc-action-hover`,
 * `--cc-action-text`, `--cc-link`, `--cc-focus`). The club's color
 * (`--color-primary*`, set by `utils/cssVariables.ts`) is for navigation
 * selection only: the active tab and the selected in-page nav item
 * (Dan 2026-10-31).
 *
 * Theme-aware light/dark colors are NOT in here — those live in
 * `utils/themeUtils.ts` (`getCardBackground`, `getPrimaryText`, etc.).
 *
 * This file is for the dimensional/structural choices: how thick is a
 * border, how round is a corner, how big is a "medium" button.
 *
 * Added 2026-06-01.
 */

export const TOKENS = {
  page: {
    /** Outer page padding (responsive). */
    container: "p-4 sm:p-6 lg:p-8",
    /**
     * Pages are FULL WIDTH (Dan 2026-10-31): a centered max-w-7xl left ~270px
     * of empty gutter on each side of a wide screen, and every page capped at
     * a different width, so headings jumped left and right between pages.
     * The only cap left is `formWidth`, for single-column forms that would
     * otherwise stretch inputs across the screen. It is LEFT-aligned, never
     * `mx-auto`, so a page's heading always starts at the same x.
     */
    formWidth: "max-w-4xl",
    /**
     * A page's topic search in PageHeading's `search` slot: full width on a
     * phone (under the title), one fixed width beside the title from sm up.
     * Dan 2026-11-01: on Memberships the search sat on its own row, pushed
     * below the title with a "Search:" label, and other pages did it too.
     */
    searchWidth: "w-full sm:w-72 lg:w-80",
    /** Bottom margin under page H1 before the first section. */
    headingMargin: "mb-6 sm:mb-8",
  },

  card: {
    /** All section cards. Consistent rounding across the app. */
    radius: "rounded-lg",
    /**
     * Padding buckets — pick the one that matches the visual
     * weight of the card. `md` is the default and matches the
     * `p-4 sm:p-6` pattern that's already dominant.
     *
     * `none` exists because a card frame whose content must bleed to the
     * edge (a full-width table, a disclosure header that is itself the
     * button, an image strip) cannot be expressed by overriding padding
     * via `className`: Card composes className last in the STRING, but
     * Tailwind resolves same-specificity utilities by stylesheet source
     * order, where `p-3` wins over `p-0` regardless of class order. Four
     * such containers were left hand-rolled in the 2026-08-23 sweep for
     * exactly this reason — pad the inner element instead.
     */
    padding: {
      none: "",           // Full-bleed content (tables, disclosure headers)
      sm: "p-3",          // Nested sub-cards, dense list items
      md: "p-4 sm:p-6",   // Standard section card (responsive)
      lg: "p-6 sm:p-8",   // Hero / featured card
    },
    /** Default border treatment when `border` prop is true. */
    border: "border",
    /** Default shadow level when `shadow` prop is set. */
    shadow: {
      none: "",
      sm: "shadow-sm",
      md: "shadow-md",
      lg: "shadow-lg",
    },
  },

  /**
   * The titled strip across the top of a full-bleed card (CardHeader).
   * Horizontal padding matches card.padding.md so the title lines up with a
   * padded card's content; py-3 replaces the two strips the 10-31 sweep made
   * (heading-margin-as-bottom-gap vs `!mb-0`).
   */
  cardHeader: {
    base: "flex items-center justify-between gap-3 px-4 py-3 sm:px-6",
  },

  heading: {
    /** Page-level H1 (one per page). */
    page: "text-2xl sm:text-3xl font-bold",
    /** Top-level section H2 (a few per page). */
    section: "text-lg font-semibold",
    /** Sub-section H3 (under a section heading). */
    subsection: "text-base font-semibold",
    /** Gap below a section heading before its content. */
    sectionMargin: "mb-4",
  },

  button: {
    /**
     * Shared base: every button gets these regardless of variant/size.
     * `font-medium` is the default weight; `lg` upgrades to semibold.
     */
    base: "inline-flex items-center justify-center gap-2 border border-transparent font-medium transition-colors disabled:opacity-50 disabled:cursor-not-allowed focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-[var(--cc-focus)]",
    /**
     * Corner radius bucket. Split out of `base` because a caller CANNOT
     * override it through className: Tailwind resolves same-specificity
     * utilities by stylesheet SOURCE order, and .rounded-md is emitted AFTER
     * .rounded-full (measured in the built CSS: rounded-full @45249,
     * rounded-md @45316). So <Button className="rounded-full"> silently
     * rendered a rounded-md button, which is why every pill-shaped segmented
     * filter in the app is still hand-rolled.
     */
    radius: {
      md: "rounded-md",
      full: "rounded-full",
      none: "",
    },
    /**
     * Four size buckets. These exactly match the dominant patterns
     * found in the audit:
     *   xs — inline icon-only / status badges
     *   sm — dense inline buttons (tables, toolbars)
     *   md — standard form buttons (the most common bucket)
     *   lg — primary CTAs (save, submit, "Register Now")
     */
    /**
     * Square padding for icon-only buttons. Measured across the app rather
     * than invented: of 215 icon-only buttons, p-2 (79) and p-1 (60) dominate,
     * with p-1.5 (25) and p-3 next, so the four buckets line up with the sizes
     * scale. The horizontal padding in `sizes` is wrong for a lone icon — it
     * makes a square control oblong.
     */
    iconSizes: {
      xs: "p-1",
      sm: "p-1.5",
      md: "p-2",
      lg: "p-3",
    },
    sizes: {
      xs: "px-2 py-1 text-xs",
      sm: "px-3 py-1.5 text-sm",
      md: "px-4 py-2 text-sm",
      lg: "px-6 py-3 text-base font-semibold",
    },
    /**
     * Variants — only the class fragments that differ between variants.
     * Composed with `base` + `sizes[size]`.
     */
    variants: {
      // Platform action color, never the club's (Dan 2026-10-31).
      primary:
        "bg-[var(--cc-action)] text-[var(--cc-action-text)] hover:bg-[var(--cc-action-hover)] active:bg-[var(--cc-action-hover)]",
      secondary:
        "border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-700 dark:text-gray-200 hover:bg-gray-50 dark:hover:bg-gray-600",
      // Action colors (2026-10-08): export/download and save each have their
      // own color family so they stand out from the club's main color. The
      // club can change both under Settings > Brand & Theme; the variables
      // (and readable text color) are set in utils/cssVariables.ts. `save`
      // is blue until the club picks a save color; use it for every Save
      // and Update button.
      export:
        "bg-[var(--color-export)] text-[var(--color-export-text)] hover:bg-[var(--color-export-hover)] active:bg-[var(--color-export-active)]",
      save:
        "bg-[var(--color-save)] text-[var(--color-save-text)] hover:bg-[var(--color-save-hover)] active:bg-[var(--color-save-active)]",
      danger:
        "bg-red-600 text-white hover:bg-red-700 active:bg-red-800",
      // Soft/outline danger — light-red fill + red text, dark-mode aware.
      // For lower-emphasis destructive-ish actions (e.g. Schedule Cancellation)
      // that sit inline next to neutral buttons.
      dangerSoft:
        "border border-red-300 dark:border-red-800 bg-red-50 dark:bg-red-900/20 text-red-700 dark:text-red-300 hover:bg-red-100 dark:hover:bg-red-900/40",
      // Ghost is a link-style button: platform link color, which index.css
      // already flips per theme (blue-600 light, blue-400 dark), so a club's
      // navy brand can no longer make it invisible on dark backgrounds. The
      // hover fill is neutral in both themes.
      ghost:
        "text-[var(--cc-link)] hover:bg-gray-100 dark:hover:bg-gray-700",
      // Quiet gray, for icon-only chrome: a modal or drawer close ×, a row's
      // overflow menu. Ghost is link-blue, which made every close × look like
      // a link, so the 10-31 sweep left them hand-rolled.
      neutral:
        "text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-200 hover:bg-gray-100 dark:hover:bg-gray-700",
      // A red text action ("Remove", "Clear") that sits inline, where a solid
      // danger button would shout.
      dangerGhost:
        "text-red-600 dark:text-red-400 hover:bg-red-50 dark:hover:bg-red-900/20",
    },
  },

  input: {
    /** Shared base — colors come from getInputStyles(isDarkMode). */
    base: "w-full rounded-md focus:outline-none focus:ring-2 focus:ring-[var(--cc-focus)] focus:border-transparent",
    /**
     * Size buckets. `md16` is md's padding with 16px text: the compact box
     * for a page where the text must stay full size. It is a bucket rather
     * than `className="text-base"` on an md input because two font-size
     * utilities on one element resolve by stylesheet order, not by which was
     * written last. iOS Safari also zooms the page when an input under 16px
     * takes focus, so prefer md16 on anything used on a phone.
     */
    sizes: {
      sm: "px-2 py-1 text-sm",
      md: "px-3 py-2 text-sm",
      md16: "px-3 py-2 text-base",
      lg: "px-4 py-3 text-base",
    },
  },

  label: {
    /** Canonical form label. */
    base: "block text-sm font-medium mb-1",
    /** Smaller variant for nested / dense forms. */
    sm: "block text-xs font-medium mb-1",
    /** Marker for required fields, rendered after the label text. */
    required: "text-red-600 ml-0.5",
  },

  checkbox: {
    /** Visible checkbox sizing. Checked state uses the platform action color. */
    base: "h-4 w-4 rounded accent-[var(--cc-action)] focus:ring-2 focus:ring-[var(--cc-focus)] focus:ring-offset-2",
    /** Label text adjacent to a checkbox. */
    label: "text-sm",
  },

  toggle: {
    /** The peer / peer-checked Tailwind toggle-switch pattern. */
    wrapper: "relative inline-flex items-center cursor-pointer",
    inputClass: "sr-only peer",
    /**
     * The visual switch. Pass `checked` separately for the conditional
     * background — the rest of the styling is shared.
     */
    switch:
      "w-11 h-6 rounded-full peer peer-checked:after:translate-x-full after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-[var(--cc-action-text)] after:rounded-full after:h-5 after:w-5 after:transition-all",
  },

  table: {
    /** Whole-table layout. */
    base: "w-full text-sm",
    /** Header row treatment — `bg-gray-50 dark:bg-gray-700` via themeUtils. */
    header: "border-b",
    /** Header cell. */
    headerCell:
      "px-4 py-3 text-left font-medium uppercase tracking-wider text-xs",
    /** Body row. */
    row: "border-b transition-colors",
    /** Body cell. */
    cell: "px-4 py-3",
    /** Compact mode for dense data tables. */
    compactCell: "px-3 py-2",
  },

  /**
   * The one backdrop behind every dialog and drawer. It dims the page enough
   * that only the dialog asks for attention (Gestalt figure-ground), in light
   * and dark mode alike, and matches the management app's dialogs. Before
   * 2026-10-21 the web app used about 15 values from 10% to 90%; the notes
   * delete confirm was 10% in light mode, so the page behind barely changed.
   */
  overlay: {
    backdrop: "bg-black/80",
  },

  /**
   * Animation durations. Kept here so the whole UI feels consistent.
   * Anything snappy should use `fast`; full-screen transitions can use
   * `medium`. We don't currently have a `slow` because nothing should
   * be that slow.
   */
  motion: {
    fast: "transition-all duration-150",
    medium: "transition-all duration-300",
  },
} as const;

/** Convenience type — useful in helper components. */
export type CardPadding = keyof typeof TOKENS.card.padding;
export type CardShadow = keyof typeof TOKENS.card.shadow;
export type ButtonSize = keyof typeof TOKENS.button.sizes;
export type ButtonVariant = keyof typeof TOKENS.button.variants;

/**
 * The same classes <Button> composes, for the rare control that must stay an
 * <a> (a real file link with href/download) but should look like a button.
 * Anything clickable that is not a link uses <Button> itself.
 */
export function buttonClasses(
  variant: keyof typeof TOKENS.button.variants,
  size: keyof typeof TOKENS.button.sizes = "md",
): string {
  return [
    TOKENS.button.base,
    TOKENS.button.radius.md,
    TOKENS.button.sizes[size],
    TOKENS.button.variants[variant],
  ].join(" ");
}
export type ButtonRadius = keyof typeof TOKENS.button.radius;
export type InputSize = keyof typeof TOKENS.input.sizes;
